import DeleteIcon from '@mui/icons-material/Delete';
import {
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
  Autocomplete,
  Box,
} from '@mui/material';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import { useMemo } from 'react';
import { FormValues } from './constants/orderForm.constants';
import { ProductDto } from 'desk-common';
import { formatCurrency } from '../../utils/functions/formatCurrency';

interface Props {
  index: number;
  control: Control<FormValues>;
  register: UseFormRegister<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  errors: FieldErrors<FormValues>;
  products: ProductDto[];
  items: FormValues['items'];
  selectedProductIds: number[];
  isReadOnly: boolean;
  isEditable: boolean;
  remove: (index: number) => void;
}

export const OrderItemRow = ({
  index,
  control,
  register,
  setValue,
  errors,
  products,
  items,
  selectedProductIds,
  isReadOnly,
  isEditable,
  remove,
}: Props) => {
  const item = items[index];

  const price = Number(item?.price || 0);
  const quantity = Number(item?.quantity || 0);
  const subtotal = price * quantity;

  const productMap = useMemo(
    () => new Map(products.map((p) => [p.id, p])),
    [products]
  );

  const selectedProductSet = useMemo(
    () => new Set(selectedProductIds),
    [selectedProductIds]
  );

  const availableProducts = useMemo(() => {
    return products.filter((product) => {
      if (product.id === item?.productId) {
        return true;
      }
      return product.active && !selectedProductSet.has(product.id);
    });
  }, [products, selectedProductSet, item?.productId]);

  const selectedProduct =
    item?.productId != null ? (productMap.get(item.productId) ?? null) : null;

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 3,
        backgroundColor: '#fff',
        border: '1px solid #eee',
        transition: 'all .2s ease',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        },
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, md: 5 }}>
          <Controller
            control={control}
            name={`items.${index}.productId`}
            rules={{ required: 'Seleccione un producto' }}
            render={({ field: controllerField }) => (
              <Autocomplete<ProductDto>
                disabled={isReadOnly}
                options={availableProducts}
                value={selectedProduct}
                onChange={(_, value) => {
                  controllerField.onChange(value?.id ?? null);

                  setValue(`items.${index}.price`, Number(value?.price ?? 0));
                }}
                getOptionLabel={(option) =>
                  `${option.description.toUpperCase()} (${
                    option.providerName
                  })${!option.active ? ' (DESHABILITADO)' : ''}`
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.description.toUpperCase()}{' '}
                        {!option.active && '(DESHABILITADO)'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary' }}
                      >
                        {option.providerName}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Producto"
                    fullWidth
                    error={Boolean(errors.items?.[index]?.productId)}
                    helperText={errors.items?.[index]?.productId?.message}
                  />
                )}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 6, md: 2 }}>
          <TextField
            label="Precio"
            value={formatCurrency(price)}
            disabled
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 6, md: 2 }}>
          <TextField
            label="Cantidad"
            type="number"
            disabled={isReadOnly}
            fullWidth
            error={Boolean(errors.items?.[index]?.quantity)}
            helperText={errors.items?.[index]?.quantity?.message}
            slotProps={{
              htmlInput: { min: 1, step: 1 },
            }}
            {...register(`items.${index}.quantity`, {
              valueAsNumber: true,
              required: 'Ingrese una cantidad',
              min: {
                value: 1,
                message: 'Debe ser mayor que 0',
              },
              validate: {
                validate: (value) =>
                  (Number.isInteger(value) && value > 0) ||
                  'Debe ser un entero positivo',
              },
              onChange: (e) => {
                const value = Number(e.target.value);
                if (value < 0) {
                  e.target.value = 1;
                }
              },
            })}
          />
        </Grid>

        <Grid size={{ xs: 6, md: 2 }}>
          <Typography fontWeight={800} fontSize={16}>
            {formatCurrency(subtotal)}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            Subtotal
          </Typography>
        </Grid>

        <Grid size={{ xs: 6, md: 1 }}>
          {isEditable && (
            <IconButton
              onClick={() => remove(index)}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: '#fff',
                },
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};
